import React from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Phone, Mail, MessageCircle, MapPin, Clock, Facebook, Instagram, Twitter, Linkedin } from "lucide-react";
import Layout from "../components/Layout";

const STR = {
  es: {
    kicker: "Contacto",
    h1Pre: "Estamos",
    h1Accent: "aquí para ti",
    h1Post: ".",
    sub: "Habla con un asesor real. Soporte bilingüe ES/EN, 7 días a la semana.",
    phoneTitle: "Teléfono",
    phoneSub: "Atención al cliente · Lun–Vie 9:00 AM – 6:30 PM · Sáb 9:00 AM – 2:00 PM CT",
    waTitle: "WhatsApp",
    waSub: "Respuesta promedio en menos de 5 minutos",
    emailTitle: "Correo",
    emailSub: "Para temas no urgentes · respuesta en 24h",
    addressTitle: "Dirección corporativa",
    socialKicker: "Síguenos",
    socialSub: "Tips de ahorro de energía, nuevos planes y novedades.",
    hoursTitle: "Horario de atención",
    weekdays: "Lunes a Viernes",
    weekend: "Sábados",
    chatKicker: "Chat en vivo",
    chatTitle: "¿Prefieres escribir?",
    chatSub: "Nuestro asistente AI responde al instante y te conecta con un agente humano cuando lo necesites.",
    chatCta: "Hablar ahora",
  },
  en: {
    kicker: "Contact",
    h1Pre: "We're",
    h1Accent: "here for you",
    h1Post: ".",
    sub: "Talk to a real advisor. Bilingual support EN/ES.",
    phoneTitle: "Phone",
    phoneSub: "Customer service · Mon–Fri 9:00 AM – 6:30 PM · Sat 9:00 AM – 2:00 PM CT",
    waTitle: "WhatsApp",
    waSub: "Average response in less than 5 minutes",
    emailTitle: "Email",
    emailSub: "For non-urgent matters · 24h response",
    addressTitle: "Corporate address",
    socialKicker: "Follow us",
    socialSub: "Energy-saving tips, new plans and news.",
    hoursTitle: "Business hours",
    weekdays: "Monday to Friday",
    weekend: "Saturday",
    chatKicker: "Live chat",
    chatTitle: "Prefer to type?",
    chatSub: "Our AI assistant answers instantly and connects you with a human agent when needed.",
    chatCta: "Talk now",
  },
};

export default function Contact() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "en";
  const s = STR[lang];

  return (
    <Layout>
      <Helmet>
        <title>{s.kicker} — Velcor Energy</title>
      </Helmet>

      <section className="max-w-[1100px] mx-auto px-5 pt-10 md:pt-16 pb-28 md:pb-32" data-testid="contact-page">
        <div className="text-center mb-10 md:mb-14">
          <span className="badge-pill mb-3">{s.kicker}</span>
          <h1
            className="display text-velcor-ink mx-auto leading-[1.04]"
            style={{ fontSize: "clamp(34px, 4.4vw, 56px)", maxWidth: 720, letterSpacing: "-0.024em" }}
          >
            {s.h1Pre} <span className="display-accent">{s.h1Accent}</span>{s.h1Post}
          </h1>
          <p className="mt-4 text-slate-500 text-[14.5px] md:text-[16px] max-w-[560px] mx-auto leading-relaxed">
            {s.sub}
          </p>
        </div>

        {/* Primary contact channels */}
        <div className="grid md:grid-cols-3 gap-4 md:gap-5 mb-8">
          <ChannelCard
            href="tel:+18007156516"
            icon={Phone}
            title={s.phoneTitle}
            value="(800) 715-6516"
            sub={s.phoneSub}
            tone="navy"
            testid="contact-phone"
          />
          <ChannelCard
            href="https://wa.me/17132329748?text=Hola%20Velcor"
            external
            icon={MessageCircle}
            title={s.waTitle}
            value="(713) 232-9748"
            sub={s.waSub}
            tone="green"
            testid="contact-wa"
          />
          <ChannelCard
            href="mailto:support@velcorenergy.com"
            icon={Mail}
            title={s.emailTitle}
            value="support@velcorenergy.com"
            sub={s.emailSub}
            tone="blue"
            testid="contact-email"
          />
        </div>

        {/* Live Chat banner — subtle CTA opening the floating widget */}
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent("velcor:open-chat"))}
          className="group w-full text-left bg-white rounded-2xl border border-slate-200 hover:border-velcor-green hover:shadow-[0_8px_24px_-12px_rgba(15,29,61,0.15)] transition-all p-5 md:p-6 mb-8 flex items-center gap-4"
          data-testid="contact-livechat-banner"
        >
          <span className="grid place-items-center w-12 h-12 rounded-full bg-velcor-green/10 text-velcor-green shrink-0">
            <span className="text-[22px] leading-none" aria-hidden="true">💬</span>
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1">
              {s.chatKicker}
            </div>
            <div className="text-velcor-ink font-extrabold text-[15px] md:text-[16px] leading-snug">
              {s.chatTitle}
            </div>
            <p className="text-slate-500 text-[12.5px] md:text-[13px] leading-relaxed mt-0.5 max-w-[640px]">
              {s.chatSub}
            </p>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-velcor-green text-white font-extrabold text-[13px] group-hover:bg-velcor-green/90 shrink-0">
            {s.chatCta} →
          </span>
        </button>

        {/* Address + Hours */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-5 mb-10">
          <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="contact-address">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="grid place-items-center w-9 h-9 rounded-full bg-velcor-green/10 text-velcor-green">
                <MapPin className="w-4 h-4" strokeWidth={2.2} />
              </span>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500">
                {s.addressTitle}
              </div>
            </div>
            <div className="text-velcor-ink font-bold text-[14px] leading-relaxed">
              Velcor Energy LLC<br />
              440 Louisiana St, Ste 1200<br />
              Houston, TX 77002-1063<br />
              United States
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="contact-hours">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="grid place-items-center w-9 h-9 rounded-full bg-blue-50 text-blue-600">
                <Clock className="w-4 h-4" strokeWidth={2.2} />
              </span>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500">
                {s.hoursTitle}
              </div>
            </div>
            <dl className="space-y-2">
              <div className="flex items-baseline justify-between text-[13px]">
                <dt className="text-slate-600 font-medium">{s.weekdays}</dt>
                <dd className="text-velcor-ink font-bold tabular-nums">9:00 AM – 6:30 PM CT</dd>
              </div>
              <div className="flex items-baseline justify-between text-[13px]">
                <dt className="text-slate-600 font-medium">{s.weekend}</dt>
                <dd className="text-velcor-ink font-bold tabular-nums">9:00 AM – 2:00 PM CT</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Social */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-7 text-center" data-testid="contact-social">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-2">
            {s.socialKicker}
          </div>
          <h3 className="text-velcor-ink mb-2" style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.015em" }}>
            @velcorenergy
          </h3>
          <p className="text-slate-500 text-[13px] mb-5 max-w-[440px] mx-auto">
            {s.socialSub}
          </p>
          <div className="flex justify-center gap-3 flex-wrap">
            <SocialBtn href="https://www.facebook.com/share/1B5itAuGZS/?mibextid=wwXIfr" icon={Facebook} label="Facebook" />
            <SocialBtn href="https://www.instagram.com/velcorenergy?igsh=MTZ3cHBmbnJvZGxicg%3D%3D&utm_source=qr" icon={Instagram} label="Instagram" />
            <SocialBtn href="https://twitter.com/velcorenergy" icon={Twitter} label="Twitter / X" />
            <SocialBtn href="https://linkedin.com/company/velcorenergy" icon={Linkedin} label="LinkedIn" />
          </div>
        </div>
      </section>
    </Layout>
  );
}

function ChannelCard({ href, external, icon: Icon, title, value, sub, tone, testid }) {
  const tones = {
    navy: { bg: "bg-[#0f1d3d]/8", text: "text-[#0f1d3d]", border: "hover:border-[#0f1d3d]" },
    green: { bg: "bg-velcor-green/10", text: "text-velcor-green", border: "hover:border-velcor-green" },
    blue: { bg: "bg-blue-50", text: "text-blue-600", border: "hover:border-blue-500" },
  };
  const t = tones[tone] || tones.navy;
  const Cmp = external ? "a" : "a";
  const props = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <Cmp
      href={href}
      {...props}
      className={`group bg-white rounded-2xl border border-slate-200 ${t.border} p-5 md:p-6 transition-all hover:shadow-[0_8px_24px_-12px_rgba(15,29,61,0.15)] hover:-translate-y-0.5`}
      data-testid={testid}
    >
      <div className={`grid place-items-center w-11 h-11 rounded-full ${t.bg} ${t.text} mb-4`}>
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </div>
      <div className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1">
        {title}
      </div>
      <div className="text-velcor-ink font-extrabold text-[16px] tabular-nums mb-1.5">
        {value}
      </div>
      <p className="text-slate-500 text-[12.5px] leading-relaxed">{sub}</p>
    </Cmp>
  );
}

function SocialBtn({ href, icon: Icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="grid place-items-center w-11 h-11 rounded-full bg-slate-50 border border-slate-200 text-slate-700 hover:bg-velcor-green hover:text-white hover:border-velcor-green transition-all"
      data-testid={`social-${label.toLowerCase().split(" ")[0]}`}
    >
      <Icon className="w-4 h-4" strokeWidth={2} />
    </a>
  );
}
